<template>
  <b-card class="mail-settings">
    <h2 class="mail-settings__title">
      {{ $t('settings.sections.email.title') }}
    </h2>
    <p class="text-muted">
      {{ $t('settings.sections.email.description') }}
    </p>

    <b-alert
      :show="Boolean(loadError)"
      variant="danger">
      {{ loadError }}
    </b-alert>

    <div
      v-if="loading"
      class="text-center p-4">
      <b-spinner small />
      {{ $t('settings.loading') }}
    </div>

    <b-form
      v-else
      @submit.stop.prevent="save">
      <b-alert
        :show="!configured"
        variant="info">
        {{ $t('settings.sections.email.notConfigured') }}
      </b-alert>

      <h3 class="mail-settings__subtitle">
        {{ $t('settings.sections.email.smtp') }}
      </h3>
      <b-form-row>
        <b-col md="8">
          <b-form-group
            :label="$t('settings.sections.email.host')"
            label-for="mail-host">
            <b-form-input
              id="mail-host"
              v-model.trim="form.host"
              :state="state('host')"
              placeholder="smtp.example.com"
              required />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            :label="$t('settings.sections.email.port')"
            label-for="mail-port">
            <b-form-input
              id="mail-port"
              v-model.number="form.port"
              :state="state('port')"
              type="number"
              min="1"
              max="65535"
              required />
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group>
        <b-form-checkbox v-model="form.auth">
          {{ $t('settings.sections.email.auth') }}
        </b-form-checkbox>
        <b-form-checkbox v-model="form.startTls">
          {{ $t('settings.sections.email.startTls') }}
        </b-form-checkbox>
      </b-form-group>

      <b-form-row v-if="form.auth">
        <b-col md="6">
          <b-form-group
            :label="$t('settings.sections.email.username')"
            label-for="mail-username">
            <b-form-input
              id="mail-username"
              v-model.trim="form.username"
              autocomplete="off" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('settings.sections.email.password')"
            :description="passwordSet ? $t('settings.sections.email.passwordKept') : ''"
            label-for="mail-password">
            <b-form-input
              id="mail-password"
              v-model="form.password"
              :placeholder="passwordSet ? '••••••••' : ''"
              type="password"
              autocomplete="new-password" />
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group
        :label="$t('settings.sections.email.from')"
        label-for="mail-from">
        <b-form-input
          id="mail-from"
          v-model.trim="form.from"
          :state="state('from')"
          type="email"
          required />
      </b-form-group>

      <b-form-group
        :label="$t('settings.sections.email.bcc')"
        :description="$t('settings.sections.email.addressesHelp')"
        label-for="mail-bcc">
        <b-form-textarea
          id="mail-bcc"
          v-model="form.bcc"
          rows="2" />
      </b-form-group>

      <hr>

      <h3 class="mail-settings__subtitle">
        {{ $t('settings.sections.email.redirection') }}
      </h3>
      <b-form-checkbox
        v-model="form.redirectEnabled"
        switch>
        {{ $t('settings.sections.email.redirectEnabled') }}
      </b-form-checkbox>
      <b-alert
        :show="form.redirectEnabled"
        variant="warning"
        class="mt-2">
        {{ $t('settings.sections.email.redirectWarning') }}
      </b-alert>
      <b-form-group
        :label="$t('settings.sections.email.redirectTo')"
        :description="$t('settings.sections.email.addressesHelp')"
        :invalid-feedback="$t('settings.sections.email.redirectRequired')"
        :state="state('redirectTo')"
        label-for="mail-redirect-to"
        class="mt-3">
        <b-form-textarea
          id="mail-redirect-to"
          v-model="form.redirectTo"
          :state="state('redirectTo')"
          :disabled="!form.redirectEnabled"
          rows="2" />
      </b-form-group>

      <b-alert
        :show="Boolean(saveMessage)"
        :variant="saveSuccess ? 'success' : 'danger'"
        class="mt-3">
        {{ saveMessage }}
      </b-alert>

      <div class="mail-settings__actions">
        <small
          v-if="updatedAt"
          class="text-muted">
          {{ $t('settings.sections.email.lastUpdate', { date: formattedUpdatedAt, user: updatedBy || '-' }) }}
        </small>
        <b-button
          :disabled="saving"
          type="submit"
          variant="primary">
          <b-spinner
            v-if="saving"
            small />
          {{ $t('settings.save') }}
        </b-button>
      </div>

      <hr>

      <h3 class="mail-settings__subtitle">
        {{ $t('settings.sections.email.test') }}
      </h3>
      <p class="text-muted">
        {{ $t('settings.sections.email.testDescription') }}
      </p>
      <b-input-group>
        <b-form-input
          v-model.trim="testRecipient"
          :placeholder="$t('settings.sections.email.testRecipient')"
          type="email"
          @keyup.enter="sendTest" />
        <b-input-group-append>
          <b-button
            :disabled="testing || !testRecipient"
            variant="outline-primary"
            @click="sendTest">
            <b-spinner
              v-if="testing"
              small />
            {{ $t('settings.sections.email.sendTest') }}
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-alert
        :show="Boolean(testMessage)"
        :variant="testSuccess ? 'success' : 'danger'"
        class="mt-3">
        {{ testMessage }}
      </b-alert>
    </b-form>
  </b-card>
</template>

<script>
import { getMailSettings, saveMailSettings, sendTestMail } from '../../../services/mailSettingsService';

const toList = value => (value || '')
  .split(/[\s,;]+/)
  .map(address => address.trim())
  .filter(Boolean);

const emptyForm = () => ({
  host: '',
  port: 587,
  auth: true,
  startTls: true,
  username: '',
  password: '',
  from: '',
  bcc: '',
  redirectEnabled: false,
  redirectTo: ''
});

export default {
  data() {
    return {
      form: emptyForm(),
      configured: false,
      passwordSet: false,
      updatedAt: null,
      updatedBy: null,
      loading: false,
      loadError: '',
      saving: false,
      submitted: false,
      saveMessage: '',
      saveSuccess: false,
      testRecipient: '',
      testing: false,
      testMessage: '',
      testSuccess: false
    };
  },
  computed: {
    formattedUpdatedAt() {
      return this.updatedAt ? new Date(this.updatedAt).toLocaleString(this.$i18n.locale) : '';
    }
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.loadError = '';
      getMailSettings()
        .then(settings => this.applyResponse(settings))
        .catch(() => {
          this.loadError = this.$t('settings.sections.email.loadError');
        })
        .then(() => {
          this.loading = false;
        });
    },
    applyResponse(settings) {
      this.configured = settings.configured;
      this.passwordSet = settings.passwordSet;
      this.updatedAt = settings.updatedAt;
      this.updatedBy = settings.updatedBy;
      if (!settings.configured) return;

      this.form = {
        host: settings.host || '',
        port: settings.port,
        auth: settings.auth,
        startTls: settings.startTls,
        username: settings.username || '',
        password: '',
        from: settings.from || '',
        bcc: (settings.bcc || []).join('\n'),
        redirectEnabled: settings.redirectEnabled,
        redirectTo: (settings.redirectTo || []).join('\n')
      };
    },
    state(field) {
      if (!this.submitted) return null;
      if (field === 'redirectTo') {
        return this.form.redirectEnabled ? toList(this.form.redirectTo).length > 0 : null;
      }
      if (field === 'port') {
        return this.form.port >= 1 && this.form.port <= 65535;
      }
      return Boolean(this.form[field]);
    },
    payload() {
      return {
        host: this.form.host,
        port: this.form.port,
        auth: this.form.auth,
        startTls: this.form.startTls,
        username: this.form.auth ? this.form.username || null : null,
        password: this.form.auth ? this.form.password || null : null,
        from: this.form.from,
        bcc: toList(this.form.bcc),
        redirectEnabled: this.form.redirectEnabled,
        redirectTo: toList(this.form.redirectTo)
      };
    },
    isValid() {
      return [ 'host', 'port', 'from', 'redirectTo' ].every(field => this.state(field) !== false);
    },
    save() {
      this.submitted = true;
      this.saveMessage = '';
      if (!this.isValid()) return;

      this.saving = true;
      saveMailSettings(this.payload())
        .then(settings => {
          this.applyResponse(settings);
          this.submitted = false;
          this.saveSuccess = true;
          this.saveMessage = this.$t('settings.sections.email.saved');
        })
        .catch(error => {
          this.saveSuccess = false;
          const detail = error.response && error.response.data && error.response.data.message;
          this.saveMessage = detail || this.$t('settings.sections.email.saveError');
        })
        .then(() => {
          this.saving = false;
        });
    },
    sendTest() {
      this.submitted = true;
      this.testMessage = '';
      if (!this.isValid() || !this.testRecipient) return;

      this.testing = true;
      sendTestMail(this.testRecipient, this.payload())
        .then(result => {
          this.testSuccess = result.success;
          this.testMessage = result.success
            ? this.$t('settings.sections.email.testSent', { to: this.testRecipient })
            : this.$t('settings.sections.email.testFailed', { reason: result.message });
        })
        .catch(() => {
          this.testSuccess = false;
          this.testMessage = this.$t('settings.sections.email.testError');
        })
        .then(() => {
          this.testing = false;
        });
    }
  }
};
</script>

<style scoped>
.mail-settings {
  text-align: left;
}

.mail-settings__title {
  font-size: 1.25rem;
}

.mail-settings__subtitle {
  font-size: 1.05rem;
  margin: 1rem 0 0.75rem;
}

.mail-settings__actions {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}
</style>
